import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DICTIONARIES } from "@/i18n/translations";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Undo2, Search, Save } from "lucide-react";

const LANG_CODES = ["en", "fr", "el", "pl"];
const LANG_LABELS = { en: "English", fr: "Français", el: "Ελληνικά", pl: "Polski" };

// Flatten an object into { "a.b.c.0": "leaf value", ... } — only string leaves
function flatten(obj, prefix = "") {
  const out = {};
  if (obj === null || obj === undefined) return out;
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => Object.assign(out, flatten(v, prefix ? `${prefix}.${i}` : String(i))));
    return out;
  }
  if (typeof obj === "object") {
    for (const k of Object.keys(obj)) {
      Object.assign(out, flatten(obj[k], prefix ? `${prefix}.${k}` : k));
    }
    return out;
  }
  if (typeof obj === "string") {
    out[prefix] = obj;
  }
  return out;
}

const AdminTranslations = () => {
  const defaultDicts = DICTIONARIES;
  const [overrides, setOverrides] = useState({}); // { "key|lang": value }
  const [drafts, setDrafts] = useState({}); // { "key|lang": value } — user edits pending save
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load current overrides on mount
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/translations");
      const map = {};
      for (const e of data.entries || []) map[`${e.key}|${e.lang}`] = e.value;
      setOverrides(map);
      setDrafts({});
    } catch (_e) {
      toast.error("Failed to load overrides.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Build the union of keys across all 4 languages
  const { keys, groups } = useMemo(() => {
    const allKeys = new Set();
    const perLang = {};
    for (const l of LANG_CODES) {
      perLang[l] = flatten(defaultDicts[l] || {});
      Object.keys(perLang[l]).forEach((k) => allKeys.add(k));
    }
    const sorted = Array.from(allKeys).sort();
    // Group by top-level section (first dot segment)
    const grp = {};
    for (const k of sorted) {
      const g = k.split(".")[0];
      (grp[g] = grp[g] || []).push(k);
    }
    return { keys: sorted, groups: grp, defaults: perLang };
  }, [defaultDicts]);

  const defaults = useMemo(() => {
    const perLang = {};
    for (const l of LANG_CODES) perLang[l] = flatten(defaultDicts[l] || {});
    return perLang;
  }, [defaultDicts]);

  const resolve = useCallback((key, lang) => {
    const k = `${key}|${lang}`;
    if (k in drafts) return drafts[k];
    if (k in overrides) return overrides[k];
    return defaults[lang][key] ?? "";
  }, [drafts, overrides, defaults]);

  const isModified = useCallback((key, lang) => {
    const k = `${key}|${lang}`;
    if (k in drafts) return drafts[k] !== (overrides[k] ?? defaults[lang][key] ?? "");
    return k in overrides && overrides[k] !== (defaults[lang][key] ?? "");
  }, [drafts, overrides, defaults]);

  const dirty = Object.keys(drafts).length > 0;

  const setDraft = (key, lang, value) => {
    setDrafts((d) => ({ ...d, [`${key}|${lang}`]: value }));
  };

  const revertKey = async (key) => {
    // Delete overrides for all 4 langs on this key
    const entries = LANG_CODES.map((lang) => ({ key, lang, value: "" }));
    try {
      setSaving(true);
      await api.post("/admin/translations", { entries });
      toast.success("Reverted to default.");
      await load();
    } catch (_e) {
      toast.error("Revert failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (!dirty) return;
    const entries = Object.entries(drafts).map(([kl, value]) => {
      const idx = kl.lastIndexOf("|");
      const key = kl.slice(0, idx);
      const lang = kl.slice(idx + 1);
      // If user cleared it AND value equals default, send empty to delete override
      const isDefault = value === (defaults[lang][key] ?? "");
      return { key, lang, value: isDefault ? "" : value };
    });
    try {
      setSaving(true);
      await api.post("/admin/translations", { entries });
      toast.success(`Saved ${entries.length} change${entries.length === 1 ? "" : "s"}. Live now.`);
      await load();
    } catch (_e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Filter based on search
  const q = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    const out = {};
    for (const [g, keys] of Object.entries(groups)) {
      const matches = keys.filter((k) => {
        if (k.toLowerCase().includes(q)) return true;
        for (const lang of LANG_CODES) {
          const v = resolve(k, lang);
          if (v && v.toLowerCase().includes(q)) return true;
        }
        return false;
      });
      if (matches.length) out[g] = matches;
    }
    return out;
  }, [q, groups, resolve]);

  return (
    <div data-testid="admin-translations" className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <p className="editorial-kicker">Text content editor</p>
          <p className="text-sm text-foreground/60 mt-1">Edit any text on the site in all 4 languages. Empty = falls back to default.</p>
        </div>
        <Button
          onClick={saveAll}
          disabled={!dirty || saving}
          data-testid="admin-translations-save"
          className="h-11 px-5 bg-foreground text-background hover:bg-foreground/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : dirty ? `Save ${Object.keys(drafts).length} change${Object.keys(drafts).length === 1 ? "" : "s"}` : "No changes"}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search keys or text (any language)…"
          data-testid="admin-translations-search"
          className="pl-10 h-11"
        />
      </div>

      {loading ? (
        <p className="editorial-kicker py-10 text-center text-foreground/50">Loading…</p>
      ) : (
        <div className="space-y-10">
          {Object.entries(filteredGroups).length === 0 && (
            <p className="editorial-kicker py-10 text-center text-foreground/50">No matches.</p>
          )}
          {Object.entries(filteredGroups).map(([section, keys]) => (
            <section key={section} data-testid={`admin-tr-section-${section}`}>
              <h3 className="font-serif-display italic text-2xl mb-4">{section}</h3>
              <div className="space-y-6">
                {keys.map((k) => {
                  const anyModified = LANG_CODES.some((l) => isModified(k, l));
                  // Show as textarea when any language value is longer than 80 chars
                  const useTextarea = LANG_CODES.some((l) => (resolve(k, l) || "").length > 80);
                  return (
                    <div
                      key={k}
                      className={`surface-card p-4 ${anyModified ? "ring-1 ring-accent/50" : ""}`}
                      data-testid={`admin-tr-row-${k}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <code className="text-xs text-foreground/60 font-mono break-all">{k}</code>
                        {anyModified && (
                          <button
                            type="button"
                            onClick={() => revertKey(k)}
                            disabled={saving}
                            data-testid={`admin-tr-revert-${k}`}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-foreground/60 hover:text-accent"
                            title="Revert this key to default in all languages"
                          >
                            <Undo2 className="w-3 h-3" /> Revert
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                        {LANG_CODES.map((lang) => {
                          const val = resolve(k, lang);
                          const modified = isModified(k, lang);
                          const Comp = useTextarea ? Textarea : Input;
                          return (
                            <div key={lang}>
                              <div className="flex items-center justify-between mb-1">
                                <label className="editorial-kicker text-[10px]">{LANG_LABELS[lang]}</label>
                                {modified && <span className="text-[9px] uppercase tracking-widest text-accent">edited</span>}
                              </div>
                              <Comp
                                value={val}
                                onChange={(e) => setDraft(k, lang, e.target.value)}
                                rows={useTextarea ? 4 : undefined}
                                data-testid={`admin-tr-input-${k}-${lang}`}
                                className={`text-sm ${modified ? "border-accent/60" : ""}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTranslations;
